/**
 * Solr query helpers
 * Build legacy Solr "q" strings to match mdm_ui backend expectations.
 */

const LUCENE_SPECIAL_CHARS = /([!+\-=<>|()^~?/])/g;

export const escapeLucene = (input = '') => {
  if (!input) return '';
  return input.replace(LUCENE_SPECIAL_CHARS, '\\$1');
};

const escapeAmpersand = (input = '') => {
  if (!input) return '';
  return input.replace(/&/g, '%26');
};

const normalizeFilter = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const buildFieldQuery = (field, value) => {
  const normalized = normalizeFilter(value);
  if (!normalized) return '';
  const escaped = escapeAmpersand(escapeLucene(normalized));
  return `${field}:"${escaped}"`;
};

export const buildHcpQuery = (filters = {}, page = 1, pageSize = 25, mode = 'basic') => {
  const start = Math.max(page - 1, 0) * pageSize;
  const params = `&rows=${pageSize}&start=${start}`;

  const freeText = normalizeFilter(mode === 'advanced' ? filters.advancedText : filters.basicText);
  const terms = [];

  if (freeText) {
    terms.push(`(${escapeAmpersand(escapeLucene(freeText))})`);
  }

  const fieldFilters = [
    buildFieldQuery('professions', filters.professions),
    buildFieldQuery('specialities', filters.specialities),
    buildFieldQuery('gender', filters.gender),
    buildFieldQuery('status', filters.status),
    buildFieldQuery('state', filters.state),
    buildFieldQuery('zip5', filters.zip5),
    buildFieldQuery('npi_id', filters.npiId),
    buildFieldQuery('sfx_cd', filters.suffix),
    buildFieldQuery('is_golden_universe', filters.isGoldenUniverse),
  ].filter(Boolean);

  if (Array.isArray(filters.source) && filters.source.length > 0) {
    const sourceQuery = filters.source
      .map((src) => src && src.trim())
      .filter(Boolean)
      .map((src) => `source:*${escapeAmpersand(escapeLucene(src))}*`)
      .join(' OR ');
    if (sourceQuery) {
      fieldFilters.push(`(${sourceQuery})`);
    }
  }

  if (fieldFilters.length > 0) {
    terms.push(fieldFilters.join(' AND '));
  }

  const queryCore = terms.length > 0 ? terms.join(' AND ') : '*:*';
  const withDeletedFilter = `${queryCore} AND -is_deleted:true`;
  return `q=${withDeletedFilter}${params}`;
};

export const buildDrugQuery = (filters = {}, page = 1, pageSize = 25) => {
  const start = Math.max(page - 1, 0) * pageSize;
  const defaultParams = `&rows=${pageSize}&start=${start}`;
  const largeParams = '&rows=25';

  const terms = [];

  const name = normalizeFilter(filters.name);
  if (name) {
    const escaped = encodeURIComponent(name.toUpperCase());
    terms.push(`(${escaped})`);
  }

  const fieldFilters = [
    buildFieldQuery('brand_name', filters.brandName),
    buildFieldQuery('brand_type', filters.brandType),
    buildFieldQuery('npa_product_sum', filters.productSum),
  ].filter(Boolean);

  if (fieldFilters.length > 0) {
    terms.push(fieldFilters.join(' AND '));
  }

  if (terms.length === 0) {
    return `q=*:*${defaultParams}`;
  }

  return `q=${terms.join(' AND ')}${largeParams}`;
};

export const normalizeSolrArray = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value);
};
